import { PlayBidApiClient } from '../client/PlayBidApiClient.js';

// The SDK-wide backend bootstrap (Milestone 7.3 — architecture correction).
//
// Before this, backend integration lived entirely in the Reference
// Application's own JS - every consumer that wanted auth/config/telemetry to
// fire had to hand-wire a PlayBidApiClient and a TelemetryBridge per widget
// themselves. That meant the SDK, dropped onto any other page via CDN, was
// completely backend-blind: no auth, no config, no session/event/click/error
// reporting, ever - regardless of contract intent.
//
// `configureBackend()` is the one call that turns that on. It is
// deliberately page-scoped, not per-widget: a publisher calls it once, before
// or after creating any widgets, and every AudioWidget/VideoWidget/
// DisplayWidget constructed on the page - past or future - checks this module
// for an active connection and auto-attaches its own telemetry if one exists.
//
// A page that never calls configureBackend() gets the exact same widget
// behavior as before this milestone: zero network calls to any backend, zero
// added runtime cost beyond the bundle size itself.
//
// The connection lives on the PAGE, not in this module. boot() loads each
// widget as its own bundle, and every bundle carries its own copy of this
// module: a module-level variable set in one bundle was invisible to a widget
// built in another, so telemetry configured once never reached it. The page
// key is shared by every copy.
//
// boot() does not import this module (it stays out of `src/api/`). It leaves
// its settings under PENDING_KEY instead, and the first widget to ask for a
// client configures one from them - so the loaded widget bundles, not the
// lean boot bundle, carry the telemetry code.
const CONNECTION_KEY = '__playbidBackendConnection';
export const PENDING_KEY = '__playbidBackendConfig';

function page() {
  return typeof globalThis === 'object' && globalThis ? globalThis : {};
}

function current() {
  return page()[CONNECTION_KEY] ?? null;
}

/**
 * Activates backend integration for the rest of this page's lifetime.
 *
 * @param {object} options                 Forwarded to ApiConfiguration - see
 *   its constructor for the full shape (baseUrl, publisherId, apiKey, timeout,
 *   auth, telemetry, logging). baseUrl/publisherId are required; there is no
 *   default baseUrl, since the documented host is a placeholder in a
 *   PROPOSED contract (never point at it by accident).
 * @param {object} [services]              Injection seam for tests - the same
 *   `{ transport, logger, ... }` shape PlayBidApiClient accepts.
 * @returns {Promise<{authenticated: boolean, expiresAt: number|null}>}
 *   Resolves once the initial token exchange settles. Callers are never
 *   required to await it - widgets constructed before it resolves still
 *   attach immediately; their telemetry calls simply wait on the same
 *   in-flight token via AuthManager, exactly as every other authenticated
 *   call already does.
 */
export function configureBackend(options = {}, services = {}) {
  // Idempotent by page, not by call: a second configureBackend() call
  // replaces the connection outright (new credentials, new backend) rather
  // than layering a second client widgets might inconsistently attach to.
  current()?.client?.destroy();

  const client = new PlayBidApiClient({ config: options, services, initialAuth: options.initialAuth });
  page()[CONNECTION_KEY] = { client };

  return client.authenticate();
}

// Internal accessor - every widget constructor calls this to decide whether
// to attach its own telemetry. Never exported publicly: a widget has no
// business handing its client reference to publisher code, and a publisher
// has no reason to reach into it either (this is why AuthManager already
// never exposes a token - a Client here is a strictly larger surface).
export function getBackendClient() {
  const existing = current();
  if (existing) return existing.client;
  // Settings boot() left for the widgets (see PENDING_KEY). Consumed once, so
  // a failure here is not retried by every widget on the page.
  const pending = page()[PENDING_KEY];
  if (!pending) return null;
  delete page()[PENDING_KEY];
  try {
    configureBackend(pending).catch(() => {});
  } catch {
    return null;
  }
  return current()?.client ?? null;
}

// Read-only, safe-to-expose status - what the Reference Application's own
// inspection panel reads, and what any host page can use to show connection
// state without ever touching the client itself.
export function getBackendStatus() {
  const connection = current();
  if (!connection) return Object.freeze({ configured: false, authenticated: false, expiresAt: null });
  const auth = connection.client.authStatus;
  return Object.freeze({ configured: true, authenticated: auth.authenticated, expiresAt: auth.expiresAt });
}

// Test-only / page-teardown seam. Not part of the public SDK surface (not
// re-exported from src/index.js) - a real page never needs to "unconfigure"
// the backend mid-session, but tests need a clean slate between cases and
// something must exist to release the client's own resources (its event
// queue) if a page ever does want to disable backend integration.
export function resetBackendConnection() {
  current()?.client?.destroy();
  delete page()[CONNECTION_KEY];
  delete page()[PENDING_KEY];
}
